/**
 * Confirmation for a destructive action, in place of `window.confirm`.
 *
 * Speaks the platform's destructive-confirm language (ConfirmRecallModal,
 * CancelOrderModal): a red alert badge over the title, a warning banner naming
 * what is about to go and saying plainly that it can't be undone, then Cancel
 * next to a red confirm. The overlay, card shape and dark-mode tokens follow the
 * table's own ColumnManagerPanel so the two read as one surface.
 *
 * Mount it *inside* whatever container owns the trigger: dropdowns here close on
 * any document mousedown landing outside their container, and a confirmation
 * mounted elsewhere would pull the dropdown out from under itself.
 */

import React, { useEffect } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import { motion } from "framer-motion";

const ConfirmDeleteModal = ({
    isOpen,
    /** Dialog heading, e.g. "Remove tab". */
    title = "Are you sure?",
    /** The question itself, e.g. `Remove the "Late" tab?`. */
    headline,
    /** What the confirm actually does, and what it leaves alone. */
    description = "This cannot be undone.",
    confirmLabel = "Delete",
    busy = false,
    /** A failed action, surfaced here rather than behind the modal. */
    error = null,
    /** Raise this above a host that already sits in a high stacking layer. */
    overlayClassName = "z-[5000]",
    onCancel,
    onConfirm,
}) => {
    useEffect(() => {
        if (!isOpen) return;
        const handleKeyDown = (e) => {
            if (e.key === "Escape" && !busy) onCancel?.();
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [isOpen, busy, onCancel]);

    if (!isOpen) return null;

    return (
        <motion.div
            className={`fixed inset-0 flex items-center justify-center bg-black/40 p-4 ${overlayClassName}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            // mousedown, not click: the host dropdown's outside-click guard listens
            // on mousedown too, so closing on click would fire after it.
            onMouseDown={(e) => {
                if (e.target === e.currentTarget && !busy) onCancel?.();
            }}
        >
            <motion.div
                role="dialog"
                aria-modal="true"
                aria-label={title}
                className="w-full max-w-sm rounded-lg bg-white shadow-xl dark:bg-acs-dark-surface"
                initial={{ scale: 0.95 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.95 }}
            >
                <div className="flex flex-col items-center gap-2 px-5 pt-5">
                    <div className="rounded-full bg-Delete-red p-2.5 text-white">
                        <AlertTriangle size={24} />
                    </div>
                    <h3 className="text-base font-semibold text-gray-900 dark:text-acs-dark-text">
                        {title}
                    </h3>
                </div>

                <div className="px-5 py-4">
                    <div className="rounded-md border-l-4 border-[#b91c1c] bg-red-50 px-3 py-2.5 dark:border-red-500 dark:bg-red-950/40">
                        {headline && (
                            <p className="text-sm font-medium text-gray-900 dark:text-acs-dark-text">
                                {headline}
                            </p>
                        )}
                        <p
                            className={`text-xs text-gray-700 dark:text-acs-dark-subtext ${
                                headline ? "mt-1" : ""
                            }`}
                        >
                            {description}
                        </p>
                    </div>

                    {error && (
                        <p className="mt-3 text-xs text-red-600 dark:text-red-400">{error}</p>
                    )}
                </div>

                <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-3 dark:border-acs-dark-border">
                    <button
                        type="button"
                        disabled={busy}
                        onClick={onCancel}
                        className="rounded-full px-4 py-1.5 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 dark:text-acs-dark-text dark:hover:bg-acs-dark-hover"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        autoFocus
                        disabled={busy}
                        onClick={onConfirm}
                        className="inline-flex items-center gap-1.5 rounded-full bg-Delete-red px-4 py-1.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60"
                    >
                        {busy && <Loader2 size={14} className="animate-spin" />}
                        {confirmLabel}
                    </button>
                </div>
            </motion.div>
        </motion.div>
    );
};

export default ConfirmDeleteModal;
